export const COST_GROUPINGS = ['account', 'service', 'usage_type', 'tenant', 'region'] as const;
export const COST_GRANULARITY = ['daily', 'monthly'] as const;
export const COST_RANGES = ['30d', '3m', '6m', '12m'] as const;
export const COST_CHARTS = ['stacked', 'lines', 'area'] as const;

export type CostGrouping = (typeof COST_GROUPINGS)[number];
export type CostGranularity = (typeof COST_GRANULARITY)[number];
export type CostRange = (typeof COST_RANGES)[number];
export type CostChart = (typeof COST_CHARTS)[number];

export const GROUPING_LABELS: Record<CostGrouping, string> = {
  account: 'Account',
  service: 'AWS service',
  usage_type: 'Usage type',
  tenant: 'Tenant tag',
  region: 'Region',
};

export const RANGE_LABELS: Record<CostRange, string> = {
  '30d': 'Last 30 days',
  '3m': 'Last 3 months',
  '6m': 'Last 6 months',
  '12m': 'Last 12 months',
};
export const CHART_LABELS: Record<CostChart, string> = {
  stacked: 'Stacked bars',
  lines: 'Lines',
  area: 'Stacked area',
};

export interface CostFilters {
  account?: string[];
  service?: string[];
  usage_type?: string[];
  tenant?: string[];
  region?: string[];
}

export interface CostQuery {
  range: CostRange;
  granularity: CostGranularity;
  group_by: CostGrouping;
  filters: CostFilters;
  /** Divide every amount by the live venue count, giving cost per venue. */
  per_venue: boolean;
  top: number;
}

export interface CostSeries {
  key: string;
  label: string;
  total: number;
  previous_total: number;
  points: { date: string; amount: number }[];
}

export interface CostResult {
  currency: 'USD';
  granularity: CostGranularity;
  dates: string[];
  series: CostSeries[];
  total: number;
  previous_total: number;
  venues: number;
  dimensions: Record<CostGrouping, { key: string; label: string }[]>;
  scope?: 'organisation' | 'account';
}

export interface CostView {
  id: string;
  name: string;
  query: CostQuery;
  chart: CostChart;
  builtin?: boolean;
}

export const DEFAULT_QUERY: CostQuery = {
  range: '6m',
  granularity: 'monthly',
  group_by: 'account',
  filters: {},
  per_venue: false,
  top: 8,
};

export const BUILTIN_VIEWS: CostView[] = [
  { id: 'by-account', name: 'By account', query: DEFAULT_QUERY, chart: 'stacked', builtin: true },
  {
    id: 'by-service',
    name: 'By service',
    query: { ...DEFAULT_QUERY, group_by: 'service' },
    chart: 'stacked',
    builtin: true,
  },
  {
    id: 'prod-services',
    name: 'Prod by service',
    query: { ...DEFAULT_QUERY, group_by: 'service', filters: { account: ['openr-prod'] } },
    chart: 'stacked',
    builtin: true,
  },
  {
    id: 'daily-30',
    name: 'Last 30 days, daily',
    query: { ...DEFAULT_QUERY, range: '30d', granularity: 'daily' },
    chart: 'lines',
    builtin: true,
  },
  {
    id: 'per-venue',
    name: 'Per venue',
    query: { ...DEFAULT_QUERY, per_venue: true },
    chart: 'area',
    builtin: true,
  },
  {
    id: 'tenant-tag',
    name: 'By tenant tag',
    query: { ...DEFAULT_QUERY, group_by: 'tenant' },
    chart: 'stacked',
    builtin: true,
  },
];

export const usd = (value: number, digits = 0): string =>
  `$${value.toLocaleString('en-US', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  })}`;

export const pctChange = (current: number, previous: number): number | null =>
  previous > 0 ? ((current - previous) / previous) * 100 : null;

export const toSearchParams = (query: CostQuery): URLSearchParams => {
  const params = new URLSearchParams({
    range: query.range,
    granularity: query.granularity,
    group_by: query.group_by,
    per_venue: String(query.per_venue),
    top: String(query.top),
  });
  (Object.keys(query.filters) as CostGrouping[]).forEach((dimension) => {
    const values = query.filters[dimension];
    if (values && values.length > 0) {
      params.set(`filter.${dimension}`, values.join(','));
    }
  });
  return params;
};

export const fromSearchParams = (params: URLSearchParams): CostQuery => {
  const filters: CostFilters = {};
  COST_GROUPINGS.forEach((dimension) => {
    const raw = params.get(`filter.${dimension}`);
    if (raw) {
      filters[dimension] = raw.split(',').filter(Boolean);
    }
  });
  const pick = <T extends string>(list: readonly T[], value: string | null, fallback: T): T =>
    value && (list as readonly string[]).includes(value) ? (value as T) : fallback;
  return {
    range: pick(COST_RANGES, params.get('range'), DEFAULT_QUERY.range),
    granularity: pick(COST_GRANULARITY, params.get('granularity'), DEFAULT_QUERY.granularity),
    group_by: pick(COST_GROUPINGS, params.get('group_by'), DEFAULT_QUERY.group_by),
    filters,
    per_venue: params.get('per_venue') === 'true',
    top: Math.min(
      Math.max(Number(params.get('top') ?? DEFAULT_QUERY.top) || DEFAULT_QUERY.top, 3),
      20,
    ),
  };
};
